<template>
  <div>
    <div class="flex items-center">
      <div
        :class="{
          'w-[96px] h-[16px]': size === 'small',
          'w-[116px] h-5 mr-2': size === 'regular'
        }"
        class="relative shrink-0 text-darkPurple"
      >
        <div class="flex">
          <div
            v-for="n in 5"
            :key="n"
            class="relative"
            :class="starClass"
          >
            <!-- White background star -->
            <img
              src="@/assets/images/star_white.svg"
              alt="Star"
              class="absolute top-0 left-0"
              :class="starClass"
            />
            <!-- Clipped blue foreground star -->
            <div class="absolute top-0 left-0 overflow-hidden" :class="starClass" :style="{ clipPath: getClipPath(n) }">
              <img
                src="@/assets/images/star_blue.svg"
                alt="Star"
                :class="starClass"
              />
            </div>
          </div>
        </div>
      </div>
      <div :class="variant" class="ml-1">
        <span :class="{ 'md:text-xl': size === 'regular' }" class="font-bold">{{ $n(rating) }}</span
        ><span class="text-xs"> /5</span>
      </div>
    </div>
    <div class="mt-2 text-sm font-bold text-purple">
      {{ $n(totalRatings) }}
      <span v-if="size === 'regular'" class="text-xs font-normal text-darkPurple">{{ t('verifiedRatings') }}</span>
      <span v-if="size === 'small'" class="text-xs font-normal text-darkPurple">{{ t('ratings') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
type Variant = 'purple' | 'blue'
type Size = 'regular' | 'small'
const { t } = useI18n()
const props = withDefaults(
  defineProps<{
    rating: number
    totalRatings: number
    variant?: Variant
    size?: Size
  }>(),
  {
    variant: 'purple',
    size: 'regular'
  }
)

const variant = computed(() => ({
  'text-darkPurple': props.variant === 'purple',
  'text-trublue': props.variant === 'blue'
}))

const starClass = computed(() => ({
  'w-5 h-[20px]': props.size === 'small',
  'w-6 h-6': props.size === 'regular'
}))

const starSize = computed(() => (props.size === 'small' ? 20 : 24))
const k = 0.25
const maskOffsetPx = -1.1

const getClipPath = (n: number): string => {
  const fullStars = Math.floor(props.rating)
  const partial = props.rating - fullStars

  if (n <= fullStars) return 'inset(0 0 0 0)'

  if (n === fullStars + 1) {
    const centered = partial - 0.5
    const eased = (Math.sin(centered * Math.PI * k) + 1) / 2
    const visible = eased * starSize.value
    const hidden = starSize.value - visible - maskOffsetPx
    const clamped = Math.max(0, Math.min(hidden, starSize.value))
    return `inset(0 ${clamped.toFixed(2)}px 0 0)`
  }

  return `inset(0 ${starSize.value}px 0 0)`
}
</script>
